import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import L from 'leaflet'
import { useSnackbar } from 'notistack'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { isRouted } from '../../lib/ballad'
import { Ballad, PublicTransportStationFeature } from '@digimap/types'
import LeafletBalladRoute from '../../components/leaflet/BalladRoute'
import PublicTransportMarkers from '../../components/leaflet/PublicTransportMarkers'
import LeafletMap from '../../components/leaflet-react/components/LeafletMap'
import { LeafletContext } from '../../components/leaflet-react/contexts/LeafletContext'
import LeafletPOIsControl from '../../components/leaflet/POIsControl'
import { DEFAULT_MAP_OPTIONS } from '../../constants/leaflet'
import { getErrorMessage } from '../../lib/errors'
import {
  deleteBallad,
  fetchPublicTransportStations,
  getBalladClosestPublicTransportStations,
  toggleBalladDraft,
  updateBalladPublicTransportStations,
} from '../../services'
import DeleteButton from './components/DeleteButton'
import PublicTransportSuggestionsList from './components/PublicTransportSuggestionsList'
import ReorderablePublicTransportList from './components/ReorderablePublicTransportList'
import { formatDistance } from './lib/format'
import { getMapProviders } from '../../constants/leafletMapProviders'
import { UserContext } from '../../state/user/UserProvider'
import { isAdmin } from '../../lib/user'
import { decode } from '@mapbox/polyline'

interface AdminBalladDetailsProps {
  ballad: Ballad
  onDelete: (balladId: string) => void
  onDraftToggle: (balladId: string, draft: boolean) => void
}

export default function AdminBalladDetails(props: AdminBalladDetailsProps) {
  const { enqueueSnackbar } = useSnackbar()
  const userContext = useContext(UserContext)
  const { ballad } = props

  const [publicTransportSuggestions, setPublicTransportSuggestions] =
    useState<Object | null>(null)

  const [selectedPublicTransportStarts, setSelectedPublicTransportStarts] =
    useState<PublicTransportStationFeature[]>([])

  const [selectedPublicTransportEnds, setSelectedPublicTransportEnds] =
    useState<PublicTransportStationFeature[]>([])

  useEffect(() => {
    const fetchAndSetPublicTransportStations = async (
      ids: string[],
      isStart: boolean
    ) => {
      try {
        const { features } = await fetchPublicTransportStations(ids)
        const hashedResults: {
          [key: string]: PublicTransportStationFeature
        } = features.reduce((prev, s) => {
          return { ...prev, ...{ [s.properties._id]: s } }
        }, {})

        const sortedStations = ids.map((id, index) => {
          const { properties } = hashedResults[id]
          const newProperties = { ...properties, ...{ main: index === 0 } }
          return { ...hashedResults[id], ...{ properties: newProperties } }
        })
        if (isStart) {
          setSelectedPublicTransportStarts(sortedStations)
        } else {
          setSelectedPublicTransportEnds(sortedStations)
        }
      } catch (e) {
        enqueueSnackbar(getErrorMessage(e), { variant: 'error' })
      }
    }
    if (
      ballad.publicTransportStartIds &&
      ballad.publicTransportStartIds.length > 0
    ) {
      fetchAndSetPublicTransportStations(ballad.publicTransportStartIds, true)
    }
    if (
      ballad.publicTransportEndIds &&
      ballad.publicTransportEndIds.length > 0
    ) {
      fetchAndSetPublicTransportStations(ballad.publicTransportEndIds, false)
    }
    // eslint-disable-next-line
  }, [])

  const { state } = useContext(LeafletContext)

  const [draft, setDraft] = useState<boolean>(props.ballad.draft)
  const [hasRoute] = useState(isRouted(ballad))
  const [showRoute, setShowRoute] = useState(hasRoute)
  const [routePoints, setRoutePoints] = useState<[number, number][]>([])

  useMemo(() => {
    if (hasRoute && ballad.track) {
      const points = decode(ballad.track)

      // Can show route only if route has more than 2 points
      if (points.length >= 2) {
        setRoutePoints(points)
        setShowRoute(true)
      } else {
        setShowRoute(false)
        console.warn('Route has less than 2 points!')
      }
    }
  }, [ballad.track, hasRoute])

  // Fit bounds to route and public transport stations on initial load
  useEffect(() => {
    if (!state.map) return

    const bounds = L.latLngBounds([])

    // Add route points to bounds
    if (routePoints.length > 0) {
      routePoints.forEach((point) => {
        bounds.extend(point)
      })
    }

    // Add public transport stations to bounds
    const allStations = [
      ...selectedPublicTransportStarts,
      ...selectedPublicTransportEnds,
    ]
    allStations.forEach((station) => {
      const [lng, lat] = station.geometry.coordinates
      bounds.extend([lat, lng])
    })

    // Only fit bounds if we have at least one point
    if (bounds.isValid()) {
      state.map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [
    state.map,
    routePoints,
    selectedPublicTransportStarts,
    selectedPublicTransportEnds,
  ])

  return (
    <Grid container spacing={0} className='full-height-without-toolbar'>
      <Grid item xs={4} style={{ overflow: 'auto', height: '100%' }}>
        <div style={{ height: '100%' }}>
          <Typography variant='h4' component='h1'>
            <Link to={`/ballads/${ballad._id}`}>{ballad.name}</Link>
          </Typography>
          {ballad.distance && (
            <Typography component='p'>
              Distance: {formatDistance(ballad.distance)}
            </Typography>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={draft}
                onChange={async (e) => {
                  setDraft(e.target.checked)
                  try {
                    const { ballad: updatedBallad } = await toggleBalladDraft(
                      ballad._id,
                      e.target.checked
                    )
                    props.onDraftToggle(updatedBallad._id, updatedBallad.draft)
                    enqueueSnackbar('Draft value successfully toggled', {
                      variant: 'success',
                    })
                  } catch (e) {
                    enqueueSnackbar(getErrorMessage(e), { variant: 'error' })
                  }
                }}
                value='draft'
                color='primary'
              />
            }
            label='Is Draft'
          />
          <DeleteButton
            onAgree={async () => {
              try {
                await deleteBallad(ballad._id)
                enqueueSnackbar('Ballad successfully deleted', {
                  variant: 'success',
                })
                props.onDelete(ballad._id)
              } catch (e) {
                enqueueSnackbar(getErrorMessage(e), { variant: 'error' })
              }
            }}
          />
          <Button
            variant='outlined'
            size='small'
            href={`${window.location.href}/edit`}
          >
            Edit
          </Button>

          <Typography variant='h5' component='h3'>
            Public Transport
          </Typography>
          <Button
            variant='outlined'
            size='small'
            onClick={async () => {
              const startIds = selectedPublicTransportStarts.length
                ? selectedPublicTransportStarts.map((s) => s.properties._id)
                : undefined
              const endIds = selectedPublicTransportEnds.length
                ? selectedPublicTransportEnds.map((s) => s.properties._id)
                : undefined
              if (startIds || endIds) {
                try {
                  await updateBalladPublicTransportStations(ballad._id, {
                    ...(startIds && { startIds }),
                    ...(endIds && { endIds }),
                  })
                  enqueueSnackbar('Public Transport successfully saved', {
                    variant: 'success',
                  })
                } catch (e) {
                  enqueueSnackbar(getErrorMessage(e), { variant: 'error' })
                }
              }
            }}
          >
            Save Public Transport
          </Button>
          <Typography variant='h6' component='h4'>
            Start
          </Typography>
          <ReorderablePublicTransportList
            isStart={true}
            stations={selectedPublicTransportStarts}
            onReorder={(newStations) => {
              setSelectedPublicTransportStarts(newStations)
            }}
            onRemove={(selected: PublicTransportStationFeature) => {
              setSelectedPublicTransportStarts((prevSelected) =>
                prevSelected.filter(
                  (s) => s.properties._id !== selected.properties._id
                )
              )
            }}
            visible={true}
          />
          {ballad.loop === false && (
            <>
              <Typography variant='h6' component='h4'>
                End
              </Typography>
              <ReorderablePublicTransportList
                isStart={false}
                stations={selectedPublicTransportEnds}
                onReorder={(newStations) => {
                  setSelectedPublicTransportEnds(newStations)
                }}
                onRemove={(selected: PublicTransportStationFeature) => {
                  setSelectedPublicTransportEnds((prevSelected) =>
                    prevSelected.filter(
                      (s) => s.properties._id !== selected.properties._id
                    )
                  )
                }}
                visible={true}
              />
            </>
          )}
          {Array.isArray(publicTransportSuggestions) &&
          publicTransportSuggestions.length > 0 ? (
            <>
              <Typography variant='h6' component='h4'>
                Public Transport Start Suggestions:
              </Typography>
              <PublicTransportSuggestionsList
                stations={publicTransportSuggestions}
                selected={selectedPublicTransportStarts}
                onAdd={(selected: PublicTransportStationFeature) => {
                  setSelectedPublicTransportStarts((prevSelected) => [
                    ...prevSelected,
                    selected,
                  ])
                }}
                onRemove={(selected: PublicTransportStationFeature) => {
                  setSelectedPublicTransportStarts((prevSelected) =>
                    prevSelected.filter(
                      (s) => s.properties._id !== selected.properties._id
                    )
                  )
                }}
              />
              {ballad.loop === false && (
                <>
                  <Typography variant='h6' component='h4'>
                    Public Transport End Suggestions:
                  </Typography>
                  <PublicTransportSuggestionsList
                    stations={publicTransportSuggestions}
                    selected={selectedPublicTransportEnds}
                    onAdd={(selected: PublicTransportStationFeature) => {
                      setSelectedPublicTransportEnds((prevSelected) => [
                        ...prevSelected,
                        selected,
                      ])
                    }}
                    onRemove={(selected: PublicTransportStationFeature) => {
                      setSelectedPublicTransportEnds((prevSelected) =>
                        prevSelected.filter(
                          (s) => s.properties._id !== selected.properties._id
                        )
                      )
                    }}
                  />
                </>
              )}
            </>
          ) : (
            <Button
              variant='outlined'
              size='small'
              onClick={async () => {
                try {
                  const { nearestPath } =
                    await getBalladClosestPublicTransportStations(ballad._id)
                  setPublicTransportSuggestions(nearestPath)
                } catch (e) {
                  enqueueSnackbar(getErrorMessage(e), { variant: 'error' })
                }
              }}
            >
              Get Suggestions
            </Button>
          )}
        </div>
      </Grid>
      <Grid item xs={8}>
        <LeafletMap
          opts={DEFAULT_MAP_OPTIONS}
          mapProviders={getMapProviders(isAdmin(userContext))}
        />
        {showRoute && (
          <LeafletBalladRoute
            latlngs={routePoints}
            options={{
              color: '#FF5722',
            }}
            centerAndZoom={false}
          />
        )}
        <PublicTransportMarkers
          startStations={selectedPublicTransportStarts}
          endStations={selectedPublicTransportEnds}
          visible={true}
        />
        <LeafletPOIsControl />
      </Grid>
    </Grid>
  )
}
